import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import getUserSettings from '../fetch/get-user-settings'
import patchUserSettings, { UserSettingsPatchPayload } from '../fetch/patch-user-settings'
import { SettingsGetResponse } from '../schemas/settings-schema'
import { chatUxSlice } from '@/chat-common/store/chat-ux.slice'
import {
  selectCustomInstructions,
  selectDefaultJurisdiction,
  selectSettingsLastFetched,
  selectSettingsFetchIsLoading,
  selectSettingsLoaded,
} from '@/chat-common/store/chat-ux.selectors'

// Cache duration in milliseconds (5 seconds)
const SETTINGS_CACHE_DURATION = 5 * 1000

type UserSettingsFields = {
  default_jurisdiction?: string | null
  custom_instructions?: string | null
  research_contact_prompt_completed?: boolean
}

/**
 * Settings Hook
 * Hook to get company + user settings, and update user settings
 * @returns {getSettings} function to get user settings
 * @returns {updateUserSettings} function to update user settings
 */
export function useSettings() {
  const dispatch = useAppDispatch()
  const defaultJurisdiction = useAppSelector(selectDefaultJurisdiction)
  const customInstructions = useAppSelector(selectCustomInstructions)
  const lastFetched = useAppSelector(selectSettingsLastFetched)
  const isLoading = useAppSelector(selectSettingsFetchIsLoading)
  const settingsLoaded = useAppSelector(selectSettingsLoaded)

  const applyUserSettings = (settings: UserSettingsFields) => {
    if (settings.research_contact_prompt_completed !== undefined) {
      dispatch(chatUxSlice.actions.setResearchContactPromptCompleted(settings.research_contact_prompt_completed))
    }
    if (settings.default_jurisdiction !== undefined) {
      dispatch(chatUxSlice.actions.setDefaultJurisdiction({ jurisdiction: settings.default_jurisdiction ?? null }))
    }

    if (settings.custom_instructions !== undefined) {
      dispatch(chatUxSlice.actions.setCustomInstructions({ customInstructions: settings.custom_instructions ?? '' }))
    }
  }

  const applySettingsResponse = (response: SettingsGetResponse) => {
    const userSettings = response.data.user
    applyUserSettings({
      default_jurisdiction: userSettings.default_jurisdiction ?? null,
      research_contact_prompt_completed: userSettings.research_contact_prompt_completed ?? false,
      // Absent means never set, which is the same state as empty for this field.
      custom_instructions: userSettings.custom_instructions ?? '',
    })
  }

  /**
   * Get Settings
   *
   * Handles:
   * 1. Check cache and return if valid
   * 2. Request settings from server if cache invalid
   * 3. Inserting the settings into the state
   *
   * @param onSuccess
   * @param onError
   */
  const getSettings = async (onSuccess?: () => void, onError?: (e: unknown) => void) => {
    // Check if we are already loading
    if (isLoading) return

    try {
      dispatch(chatUxSlice.actions.setSettingsFetchIsLoading({ isLoading: true }))

      // Check if we have valid cached settings
      const now = Date.now()
      if (lastFetched && now - lastFetched < SETTINGS_CACHE_DURATION) {
        if (onSuccess) onSuccess()
        return
      }

      // Ask the server for settings
      const response: SettingsGetResponse = await getUserSettings()
      applySettingsResponse(response)

      dispatch(chatUxSlice.actions.setSettingsLastFetched({ lastFetched: now }))

      if (onSuccess) onSuccess()
    } catch (e) {
      if (onError) onError(e)
    } finally {
      dispatch(chatUxSlice.actions.setSettingsFetchIsLoading({ isLoading: false }))
    }
  }

  /**
   * Update User Settings
   */
  const updateUserSettings = async (userSettings: UserSettingsPatchPayload, onSuccess?: () => void, onError?: (e: unknown) => void) => {
    try {
      const response = await patchUserSettings(userSettings)
      applyUserSettings({ ...userSettings, ...response.data })

      if (onSuccess) onSuccess()
    } catch (e) {
      if (onError) onError(e)
      throw e
    }
  }

  return { getSettings, updateUserSettings, defaultJurisdiction, customInstructions, settingsLoaded }
}
